import React from 'react';
import type { CompatReportDocument } from '../types';
import { formatSeamSeparationMm } from '../helpers/formatReport';
import { DistributionChart } from './DistributionChart';

interface SinglePassViewProps {
  result: CompatReportDocument;
}

export const SinglePassView: React.FC<SinglePassViewProps> = ({ result }) => {
  const sp = result.singlePass;
  const ref = result.reference;
  const s = result.settings;

  if (!sp) {
    return (
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 text-[14px] text-heritage-grey">
        No single-pass block in this report.
      </div>
    );
  }

  const hiLo =
    s?.hiLoThresholdMm != null && Number.isFinite(s.hiLoThresholdMm)
      ? s.hiLoThresholdMm.toFixed(1)
      : null;
  const sixOClock =
    s?.sixOClockExclusionDeg != null && Number.isFinite(s.sixOClockExclusionDeg)
      ? Math.round(s.sixOClockExclusionDeg)
      : null;
  const seamApart = formatSeamSeparationMm(s?.weldSeamSeparationMm, s?.weldSeamExclusionDeg);
  const hasFitDefinition = hiLo != null && sixOClock != null && seamApart !== '—';

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#E6A100]" />
        <div className="font-heading font-bold text-blueprint-blue text-[clamp(2.5rem,8vw,4rem)] leading-none">
          {Math.round(sp.pipesBeforeStopAvg)}
          <span className="text-[20px] text-heritage-grey font-semibold ml-1.5">pipes</span>
        </div>
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mt-2">
          before the line stops, welding in arrival order
        </p>
        <p className="mt-3.5 text-[15px] text-text-grey">
          Range{' '}
          <span className="font-semibold text-blueprint-blue">
            {sp.shortest} to {sp.longest}
          </span>
          , typical <span className="font-semibold text-blueprint-blue">{sp.typical}</span>. This is a{' '}
          <span className="font-semibold text-blueprint-blue">count, not a fit rate</span>: it warns
          that raw arrival order stalls early, it does not say the pipes are bad.
        </p>
        {ref?.engineSinglePassPipes != null && (
          <div className="mt-3 border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            Cross-check: the production engine reached{' '}
            <span className="font-semibold text-blueprint-blue">
              {ref.engineSinglePassPipes} pipes
            </span>
            , inside this range.
          </div>
        )}
      </div>

      {hasFitDefinition && (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
          <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
            What &quot;fit&quot; means, physically
          </p>
          <div className="border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">
              Two ends fit when the hi-lo stays within {hiLo} mm
            </span>{' '}
            at an allowed rotation: outside the {sixOClock} degree six o&apos;clock band, with weld
            seams at least {seamApart} apart. That is the same check a fitter makes at the bevel, and
            it is the rule behind every number in this report.
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
          How far it gets
        </p>
        <DistributionChart
          distribution={sp.distribution || []}
          color="#E6A100"
          unitLabel="pipes before the line stopped"
        />
      </div>
    </div>
  );
};
